/**
 * PhotoCaptureGallery — phase 6 (document creations) surface.
 *
 * Design brief: teachers in a live classroom want to snap quickly. Optional
 * tagging/captioning happens later in phase 7 ("Review & tag"). So this screen
 * is capture-first:
 *   - Big camera button.
 *   - Every capture uploads immediately (downscale → Supabase Storage → row).
 *   - A clean gallery of thumbnails with clear "Captioned" / "Tagged" /
 *     "Needs review" status badges.
 *   - Quick-tag chip row per photo (collapsed by default) — optional inline.
 *
 * No photo-count limit. Upload progress shown per-photo.
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Edit2, Tag, Trash2, X } from 'lucide-react';
import type {
  PhotoWithUrl,
  TeachingPhotoRow,
} from '../../lib/teachingSession';
import type { StudentAvatarRecord } from '../../lib/teachingSession';

interface Props {
  language: 'en' | 'es';
  photos: PhotoWithUrl[];
  avatars: StudentAvatarRecord[];
  onCapture: (video: HTMLVideoElement) => Promise<PhotoWithUrl>;
  onToggleStudentTag: (photoId: string, studentIndex: number) => void;
  onDelete: (photo: TeachingPhotoRow) => Promise<void>;
  onJumpToReview?: () => void;
}

export function PhotoCaptureGallery({
  language,
  photos,
  avatars,
  onCapture,
  onToggleStudentTag,
  onDelete,
  onJumpToReview,
}: Props) {
  const t = (en: string, es: string) => (language === 'en' ? en : es);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [showCamera, setShowCamera] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [uploadingIds, setUploadingIds] = useState<Set<string>>(new Set());
  const [taggingPhotoId, setTaggingPhotoId] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
    };
  }, []);

  const startCamera = async () => {
    setCameraError(null);
    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
          audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }
      streamRef.current = stream;
      // Render the <video> first; the effect below attaches srcObject once
      // the element is mounted. This avoids the videoRef-is-null race when
      // the video is gated behind showCamera.
      setShowCamera(true);
    } catch (err) {
      const e = err as { name?: string };
      if (e.name === 'NotAllowedError') {
        setCameraError(
          t(
            'Camera permission denied. Allow camera access in your browser settings.',
            'Permiso de cámara denegado. Permite el acceso a la cámara en la configuración del navegador.',
          ),
        );
      } else if (e.name === 'NotFoundError') {
        setCameraError(
          t('No camera found on this device.', 'No se encontró cámara en este dispositivo.'),
        );
      } else {
        setCameraError(
          t('Could not access camera.', 'No se pudo acceder a la cámara.'),
        );
      }
    }
  };

  // Attach the live stream once the <video> is in the DOM.
  useEffect(() => {
    if (!showCamera) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;
    video.srcObject = stream;
    const handleLoaded = () => {
      video.play().catch(() => {
        /* autoplay can fail silently if the tab is backgrounded */
      });
    };
    video.addEventListener('loadedmetadata', handleLoaded);
    return () => video.removeEventListener('loadedmetadata', handleLoaded);
  }, [showCamera]);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  const handleCapture = async () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);
    const video = videoRef.current;
    try {
      // Optimistic: keep camera up, show the new thumbnail with "uploading" as it returns.
      const result = await onCapture(video);
      setUploadingIds((prev) => {
        const next = new Set(prev);
        next.delete(result.id);
        return next;
      });
    } catch (err) {
      setCameraError(
        t(
          'Could not save photo. Check your connection and try again.',
          'No se pudo guardar la foto. Verifica tu conexión e intenta de nuevo.',
        ),
      );
    } finally {
      setCapturing(false);
    }
  };

  const photoStatus = (p: PhotoWithUrl): 'needs-review' | 'tagged' | 'captioned' | 'complete' => {
    const hasCaption = p.caption && p.caption.trim().length > 0;
    const hasTags = p.student_tags.length > 0 || p.skill_tags.length > 0;
    if (hasCaption && hasTags) return 'complete';
    if (hasCaption) return 'captioned';
    if (hasTags) return 'tagged';
    return 'needs-review';
  };

  const statusBadge = (status: ReturnType<typeof photoStatus>) => {
    const map = {
      complete: { label: t('Complete', 'Completo'), fg: '#166534', bg: '#dcfce7' },
      captioned: { label: t('Captioned', 'Con descripción'), fg: '#075985', bg: '#e0f2fe' },
      tagged: { label: t('Tagged', 'Etiquetado'), fg: '#6b21a8', bg: '#f3e8ff' },
      'needs-review': { label: t('Needs review', 'Por revisar'), fg: '#92400e', bg: '#fef3c7' },
    } as const;
    const { label, fg, bg } = map[status];
    return (
      <span
        className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
        style={{ color: fg, background: bg }}
      >
        {label}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-3xl p-6 md:p-8 shadow-xl mb-4"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <Camera className="w-6 h-6" />
          {t('Document Student Work', 'Documentar Trabajo Estudiantil')}
        </h3>
        <p className="text-sm text-gray-500 italic flex-1 min-w-[180px]">
          {t(
            'Snap fast. Captions & detailed tags can wait for phase 7.',
            'Captura rápido. Descripciones y etiquetado detallado pueden esperar a la fase 7.',
          )}
        </p>
      </div>

      {/* Camera view */}
      <AnimatePresence>
        {showCamera && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="relative bg-black rounded-xl overflow-hidden mb-4"
          >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full"
              style={{ maxHeight: '420px', objectFit: 'cover' }}
            />
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-3">
              <button
                type="button"
                onClick={handleCapture}
                disabled={capturing}
                className="bg-white disabled:opacity-70 text-gray-800 font-bold px-6 py-3 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                {capturing ? '⏳' : '📸'}{' '}
                {capturing ? t('Saving…', 'Guardando…') : t('Capture', 'Capturar')}
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="bg-gray-800 text-white font-bold px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
              >
                <X className="w-4 h-4" /> {t('Done', 'Listo')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!showCamera && (
        <button
          type="button"
          onClick={startCamera}
          className="w-full bg-gradient-to-r from-[#5EBD8B] to-[#48A589] text-white font-bold text-lg py-4 px-6 rounded-2xl hover:shadow-lg transition-all flex items-center justify-center gap-2 mb-4"
        >
          <Camera className="w-6 h-6" />
          {photos.length === 0
            ? t('Take first photo', 'Tomar primera foto')
            : t('Take another photo', 'Tomar otra foto')}
        </button>
      )}

      {cameraError && (
        <p className="text-sm text-red-600 mb-3" role="alert">
          {cameraError}
        </p>
      )}

      {/* Gallery header */}
      {photos.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-700">
            {t('Photos', 'Fotos')}{' '}
            <span className="text-gray-400 font-normal">({photos.length})</span>
          </h4>
          {onJumpToReview && (
            <button
              type="button"
              onClick={onJumpToReview}
              className="text-sm text-[#2a6d5b] hover:text-[#358a73] font-semibold flex items-center gap-1"
            >
              <Edit2 className="w-4 h-4" />
              {t('Review & tag', 'Revisar y etiquetar')}
            </button>
          )}
        </div>
      )}

      {/* Gallery grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {photos.map((photo) => {
            const status = photoStatus(photo);
            const isUploading = uploadingIds.has(photo.id);
            return (
              <div
                key={photo.id}
                className="relative group rounded-xl overflow-hidden bg-gray-100"
                style={{ aspectRatio: '1 / 1' }}
              >
                <img
                  src={photo.publicUrl}
                  alt={photo.caption || 'Student work'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />

                {isUploading && (
                  <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
                    <span className="text-sm font-semibold text-gray-700">
                      {t('Uploading…', 'Subiendo…')}
                    </span>
                  </div>
                )}

                <div className="absolute top-2 left-2">{statusBadge(status)}</div>

                <div className="absolute top-2 right-2 flex gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
                  {avatars.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setTaggingPhotoId(taggingPhotoId === photo.id ? null : photo.id)
                      }
                      aria-label={t('Tag students', 'Etiquetar estudiantes')}
                      className="bg-white/95 text-gray-700 p-1.5 rounded-full shadow-md hover:shadow-lg"
                    >
                      <Tag className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => onDelete(photo)}
                    aria-label={t('Delete photo', 'Eliminar foto')}
                    className="bg-red-500/95 text-white p-1.5 rounded-full shadow-md hover:shadow-lg"
                  >
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                  </button>
                </div>

                {photo.student_tags.length > 0 && taggingPhotoId !== photo.id && (
                  <div className="absolute bottom-2 left-2 bg-black/65 text-white text-xs px-2 py-1 rounded-full flex gap-1">
                    {photo.student_tags.slice(0, 3).map((idx) => (
                      <span key={idx}>{avatars[idx]?.emoji ?? '•'}</span>
                    ))}
                    {photo.student_tags.length > 3 && (
                      <span>+{photo.student_tags.length - 3}</span>
                    )}
                  </div>
                )}

                {taggingPhotoId === photo.id && avatars.length > 0 && (
                  <div className="absolute inset-0 bg-white/95 p-2 overflow-auto">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold">
                        {t('Tag students', 'Etiquetar estudiantes')}
                      </p>
                      <button
                        type="button"
                        onClick={() => setTaggingPhotoId(null)}
                        aria-label={t('Close', 'Cerrar')}
                        className="text-gray-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      {avatars.map((avatar, idx) => {
                        const tagged = photo.student_tags.includes(idx);
                        return (
                          <button
                            type="button"
                            key={idx}
                            onClick={() => onToggleStudentTag(photo.id, idx)}
                            className={`text-2xl p-1 rounded transition-all ${
                              tagged
                                ? 'bg-green-100 ring-2 ring-green-500'
                                : 'bg-gray-100 hover:bg-gray-200'
                            }`}
                            aria-pressed={tagged}
                            aria-label={avatar.name || avatar.emoji}
                          >
                            {avatar.emoji}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {photos.length === 0 && !showCamera && (
        <p className="text-sm text-gray-500 text-center mt-4 italic">
          {t(
            'Capture the process, not just the final product.',
            'Captura el proceso, no solo el producto final.',
          )}
        </p>
      )}
    </motion.div>
  );
}
